import { useEffect, useRef, useState } from 'react';
import { post } from '../api';

interface Result {
  status: 'ready' | 'confirmed' | 'already' | 'expired' | 'cancelled' | 'invalid';
  visit?: { date: string; time: string; locality: string };
}

const MESSAGE: Record<Result['status'], (r: Result) => string> = {
  ready: (r) =>
    r.visit ? `Can you do ${r.visit.date} at ${r.visit.time}?` : 'Can you do this visit?',
  confirmed: (r) =>
    r.visit
      ? `Thanks, you're confirmed for ${r.visit.date} at ${r.visit.time}.`
      : "Thanks, you're confirmed.",
  already: () => "You've already confirmed this visit.",
  expired: () => 'This link has expired. Please call the office.',
  cancelled: () => 'This visit was cancelled.',
  invalid: () => "This link isn't valid.",
};

/**
 * The page a contractor lands on from "please confirm" in a text or email: no
 * sign-in. Opening it only shows the visit; confirming takes their tap, so a
 * mail scanner that opens links cannot confirm for them.
 */
export function ConfirmVisit() {
  const [result, setResult] = useState<Result | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  // StrictMode runs effects twice in development; look once.
  const asked = useRef(false);

  const ask = (confirm: boolean) => {
    const q = new URLSearchParams(window.location.search);
    return post<Result>('/api/public/visit-confirm', {
      business: q.get('business') ?? '',
      token: q.get('token') ?? '',
      confirm,
    });
  };

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    ask(false)
      .then(setResult)
      .catch(() => setFailed(true));
  }, []);

  async function confirm() {
    setBusy(true);
    setFailed(false);
    try {
      setResult(await ask(true));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="p-auth">
      <div className="card login" role="status">
        <h1>Visit confirmation</h1>
        {!result && !failed && <p>Checking…</p>}
        {failed && <p>Something went wrong. Please try the link again, or call the office.</p>}
        {result && <p>{MESSAGE[result.status](result)}</p>}
        {result?.visit?.locality && result.status !== 'invalid' && (
          <p className="muted">{result.visit.locality}</p>
        )}
        {result?.status === 'ready' && (
          <button type="button" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Confirming…' : "Yes, I'll be there"}
          </button>
        )}
      </div>
    </main>
  );
}
